import React, { useState, useEffect } from 'react';
import { Container, Row, Col, ListGroup, Form, Pagination, Button, InputGroup } from 'react-bootstrap';
import { PermissaoViewModel } from '../../viewmodel/usuario/PermissaoViewModel';
import { useParams } from 'react-router-dom';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { Permissao, PermissaoUtils } from '../../model/Usuario/Permissao';
import { FaArrowLeft, FaTimes } from 'react-icons/fa';
import { PerfilViewModel } from '../../viewmodel/usuario/PerfilViewModel';
import { Perfil } from '../../model/Usuario/Perfil';
import { toast } from 'react-toastify';

const itensPorPagina = 8; // permissoes exibidos por página

const PermissoesPerfis: React.FC = () => {
    const { processoId, bancaId } = useParams<{ processoId: string, bancaId: string }>();
    const { setBreadcrumbs } = useBreadcrumbs();

    const [permissoes, setPermissaos] = useState<Permissao[]>([]);
    const [permissoesSelecionados, setPermissaosSelecionados] = useState<Permissao[]>([]);

    // Estados para os filtros de permissoes e de permissoes selecionados
    const [filterPermissaos, setFilterPermissaos] = useState<string>('');
    const [filterSelecionados, setFilterSelecionados] = useState<string>('');

    // Paginação
    const [paginaAtual, setPaginaAtual] = useState(1);
    const [paginaAtualSelecionados, setPaginaAtualSelecionados] = useState(1);

    //Perfis
    const [perfis, setPerfis] = useState<Perfil[]>([]);
    const [perfilSelecionado, setPerfilSelecionado] = useState<Perfil | null>();

    useEffect(() => {
        setBreadcrumbs([
            { path: '/', breadcrumb: '🏠' },
            { path: `/permissoes-perfis`, breadcrumb: 'Permissões e Perfis' },
        ]);
    }, []);

    useEffect(() => {
        const fetchPerfis = async () => {
            try {
                const perfisRecebidos = await PerfilViewModel.obterPerfis();
                setPerfis(perfisRecebidos);
            }
            catch (error) {
                console.error(error);
            }
        }
        fetchPerfis();
    }, []);

    useEffect(() => {
        const fetchPermissaos = async () => {
            try {
                const permissoesRecebidos = await PermissaoViewModel.obterPermissoes();
                PermissaoUtils.setCategoriaForPermissoes(permissoesRecebidos);
                setPermissaos(permissoesRecebidos);
            }
            catch (error) {
                console.error(error);
            }
        }
        fetchPermissaos();
    }, []);

    // Função para paginar os itens de acordo com o filtro e a página atual
    const paginar = (itens: any[], paginaAtual: number) => {
        const startIndex = (paginaAtual - 1) * itensPorPagina;
        const endIndex = startIndex + itensPorPagina;
        return itens.slice(startIndex, endIndex);
    };

    // Filtrando permissoes e permissoes selecionados com base nos termos de busca
    const permissoesFiltrados = permissoes.filter(c => c.nome.toLowerCase().includes(filterPermissaos.toLowerCase()));
    const permissoesSelecionadosFiltrados = permissoesSelecionados.filter(c => c.nome.toLowerCase().includes(filterSelecionados.toLowerCase()));

    // Limpar o campo de busca de permissoes
    const limparFiltroPermissaos = () => setFilterPermissaos('');

    // Limpar o campo de busca de permissoes selecionados
    const limparFiltroSelecionados = () => setFilterSelecionados('');

    // Função para mover permissão de Permissões para Perfil
    const moverParaPerfil = (id: string) => {
        const permissao = permissoes.find(c => c.id === id);
        if (permissao && !permissoesSelecionados.find(c => c.id === id)) {
            setPermissaosSelecionados(prev => [...prev, permissao]);
        }
    };

    const carregarPermissoesDoPerfil = (perfilId: string) => {
        const perfil = perfis.find(p => p.id === perfilId);
        if (perfil) {
            setPermissaosSelecionados(perfil.permissoes);
        }
    };

    // Função para remover permissão do Perfil
    const removerDePerfil = (id: string) => {
        setPermissaosSelecionados(prev => prev.filter(c => c.id !== id));
    };

    const salvarPerfil = () => {
        const perfil = perfis.find(p => p.id === perfilSelecionado?.id);
        if (perfil) {
            // Filtrar apenas permissões que possuem 'id' e 'nome' definidos
            const permissoesValidas = permissoesSelecionados.filter(permissao => permissao.id && permissao.nome);
            // Remover duplicatas de permissões com base no nome
            const permissoesUnicas = Array.from(new Map(permissoesValidas.map(item => [item.nome, item])).values());

            // Atualizar as permissões do perfil com as permissões únicas e válidas
            perfil.permissoes = permissoesUnicas.map(p => ({ id: p.id as string, nome: p.nome as string }));
            handleAtualizarPerfil(perfil)
        }
    };

    const handleAtualizarPerfil = async (perfil: Perfil) => {
        try {
            const perfilAtualizado = await PerfilViewModel.atualizarPerfil(perfil.id!, { 
                nome: perfil.nome, 
                descricao: perfil.descricao, 
                permissoes: perfil.permissoes                
            });
            toast.success('Perfil atualizado com sucesso!');
            setPerfis([...perfis]); // Atualizar estado de perfis
        } catch (error) {
            toast.error('Falha ao atualizar perfil');
        }
    };

    return (
        <Container style={{ marginTop: '15px' }}>
            <Row>
                <Col>
                    {/* Listbox para selecionar um perfil */}
                    <Form.Group controlId="perfilSelect">
                        <Form.Label>Selecione um perfil</Form.Label>
                        <Form.Control
                            as="select"
                            value={perfilSelecionado?.id || ''}  // Atualizar o valor com o ID
                            onChange={(e) => {
                                const perfil = perfis.find(p => p.id === e.target.value); // Buscar o perfil pelo ID
                                setPerfilSelecionado(perfil || null); // Atualizar o estado do perfil selecionado
                                if (perfil) {
                                    carregarPermissoesDoPerfil(perfil.id as string); // Carregar as permissões do perfil selecionado
                                }
                            }}
                        >
                            <option value="">Selecione um perfil</option>
                            {perfis.map(perfil => (
                                <option key={perfil.id} value={perfil.id}>
                                    {perfil.nome}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>


                    <h4>Perfil</h4>
                    <InputGroup className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Filtrar por nome"
                            value={filterSelecionados}
                            onChange={(e) => setFilterSelecionados(e.target.value)}
                        />
                        <Button variant="outline-secondary" onClick={limparFiltroSelecionados}>
                            &times;
                        </Button>
                    </InputGroup>
                    <ListGroup>
                        {paginar(permissoesSelecionadosFiltrados, paginaAtualSelecionados).map(permissao => (
                            <ListGroup.Item key={permissao.id} className="d-flex justify-content-between align-items-center">
                                {permissao.nome}
                                <div>
                                    <Button variant="outline-secondary" size="sm" onClick={() => removerDePerfil(permissao.id)}>
                                        <FaTimes style={{ color: 'indianred' }} />
                                    </Button>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                    <Pagination style={{ marginTop: '15px' }}>
                        <Pagination.Prev onClick={() => setPaginaAtualSelecionados(prev => Math.max(prev - 1, 1))} />
                        <Pagination.Item>{paginaAtualSelecionados}</Pagination.Item>
                        <Pagination.Next onClick={() => setPaginaAtualSelecionados(prev => prev + 1)} />
                    </Pagination>
                </Col>

                {/* Coluna de Permissaos Disponíveis */}
                <Col>
                    <h4>Permissões</h4>
                    <InputGroup className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Filtrar por nome"
                            value={filterPermissaos}
                            onChange={(e) => setFilterPermissaos(e.target.value)}
                        />
                        <Button variant="outline-secondary" onClick={limparFiltroPermissaos}>
                            &times;
                        </Button>
                    </InputGroup>
                    <ListGroup>
                        {paginar(permissoesFiltrados, paginaAtual).map(permissao => (
                            <ListGroup.Item key={permissao.id} className="d-flex justify-content-between align-items-center">
                                <div >
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => moverParaPerfil(permissao.id)}
                                        disabled={!!permissoesSelecionados.find(c => c.id === permissao.id)}
                                    >
                                        <FaArrowLeft style={permissoesSelecionados.find(c => c.id === permissao.id) ? { color: 'lightgray' } : { color: 'darkgreen' }} />
                                    </Button>
                                </div>
                                {permissao.nome}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                    <Pagination style={{ marginTop: '15px' }}>
                        <Pagination.Prev onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))} />
                        <Pagination.Item>{paginaAtual}</Pagination.Item>
                        <Pagination.Next onClick={() => setPaginaAtual(prev => prev + 1)} />
                    </Pagination>
                </Col>
            </Row>

            {/* Botão para salvar as permissões do perfil */}
            <Button
                variant="primary"
                className="mt-3"
                onClick={salvarPerfil}
                disabled={!perfilSelecionado} // desabilitar caso nenhum perfil esteja selecionado
            >
                Salvar Perfil
            </Button>
        </Container>
    );
};

export default PermissoesPerfis;